import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_KEY = 'coachhub_plan_status';
const CACHE_MS = 30 * 60 * 1000;

const formatDay = (value) => (value
  ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  : '');

const readCache = () => {
  try {
    const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
    return cached && Date.now() - cached.at < CACHE_MS ? cached.data : null;
  } catch {
    return null;
  }
};

// For the institute owner: the plan ends within a week, or has ended and access stops soon.
// Read once per half hour; can be hidden for the rest of the day.
export default function PlanExpiryBanner({ role }) {
  const [status, setStatus] = useState(() => readCache());
  const today = new Date().toISOString().slice(0, 10);
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem('coachhub_plan_banner_hidden') === today; } catch { return false; }
  });

  useEffect(() => {
    if (role !== 'institute_admin' || status) return;
    axios.get(`${API}/institute/plan-status`)
      .then((res) => {
        setStatus(res.data);
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), data: res.data })); } catch { /* storage blocked */ }
      })
      .catch(() => {});
  }, [role, status]);

  if (role !== 'institute_admin' || hidden || !status) return null;
  const { state, days_left: daysLeft, expires_on: expiresOn, blocks_on: blocksOn } = status;
  const ending = state === 'expiring' && daysLeft !== null && daysLeft <= 7;
  if (!ending && state !== 'grace') return null;

  const text = state === 'grace'
    ? `Your ${status.plan_label || 'CoachHub'} plan ended on ${formatDay(expiresOn)}.${blocksOn ? ` Access stops on ${formatDay(blocksOn)} unless it is renewed.` : ''}`
    : daysLeft === 0
      ? `Your ${status.plan_label || 'CoachHub'} plan ends today.`
      : `Your ${status.plan_label || 'CoachHub'} plan ends in ${daysLeft} day${daysLeft === 1 ? '' : 's'}, on ${formatDay(expiresOn)}.`;
  const phone = (status.support?.phone || '').replace(/\D/g, '');

  return (
    <div
      className={`mb-4 flex items-start gap-3 rounded-2xl border p-3.5 text-sm ${state === 'grace' ? 'border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-100' : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100'}`}
      role="status"
      data-testid="plan-expiry-banner"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <p className="flex-1">
        {text}{' '}
        {phone && (
          <a
            className="font-semibold underline underline-offset-2"
            href={`https://wa.me/${phone.length === 10 ? `91${phone}` : phone}?text=${encodeURIComponent(`Hi, I want to renew the CoachHub plan for ${status.institute_name || 'my institute'}.`)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Renew on WhatsApp
          </a>
        )}
      </p>
      <button
        type="button"
        onClick={() => {
          setHidden(true);
          try { localStorage.setItem('coachhub_plan_banner_hidden', today); } catch { /* storage blocked */ }
        }}
        className="rounded-lg p-1 opacity-70 hover:opacity-100"
        aria-label="Hide for today"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
